import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Eye } from "lucide-react";
import { totalPeriodPercent, totalComponentPercent, validateScaleRanges } from "@/lib/grading";

import { uid, EMPTY_SETUP_STATE, ordinal } from "./setup/_helpers";
import ScopeFilterBar from "./setup/ScopeFilterBar";
import Step0Settings from "./setup/Step0Settings";
import Step1Periods from "./setup/Step1Periods";
import Step2CategoryLabel from "./setup/Step2CategoryLabel";
import Step3Grouping from "./setup/Step3Grouping";
import Step4Subjects from "./setup/Step4Subjects";
import SaveBar from "./setup/SaveBar";
import TemplatesDialog from "./setup/TemplatesDialog";
import PreviewReportCardDialog from "./PreviewReportCardDialog";

/* eslint-disable react-hooks/exhaustive-deps */

export default function GradingSetupPage() {
  const { selectedBranch, branchesForSelectedSchool, setSelectedBranchId } = useBranch();
  const branchId = selectedBranch?.id;

  /* ---------- Scope filters ---------- */
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [schoolYear, setSchoolYear] = useState(null);

  /* ---------- Page state ---------- */
  const [state, setState] = useState(EMPTY_SETUP_STATE);
  const [original, setOriginal] = useState(EMPTY_SETUP_STATE);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [myTemplates, setMyTemplates] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  const scopeType = !classId ? "all_levels" : !sectionId ? "class_level" : "section";

  /* ---------- Load lookup data ---------- */
  useEffect(() => {
    if (!branchId) { setClasses([]); setSections([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/classes", { params: { branch_id: branchId } });
        setClasses((data || []).filter((c) => c.class_type === "regular" || c.class_type === "modular"));
        const { data: sy } = await api.get("/school-years", { params: { branch_id: branchId } });
        setSchoolYear((sy || []).find((s) => s.is_active) || null);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId]);

  useEffect(() => {
    setSectionId("");
    if (!classId) { setSections([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/sections", { params: { branch_id: branchId, class_id: classId } });
        setSections(data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId, classId]);

  /* ---------- Load setup ---------- */
  const loadSetup = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/grading-setup", {
        params: {
          branch_id: branchId,
          school_year_id: schoolYear?.id || undefined,
          class_id: classId || undefined,
          section_id: sectionId || undefined,
          resolve: false,
        },
      });
      const enriched = {
        settings: { ...EMPTY_SETUP_STATE.settings, ...(data.settings || {}) },
        scale_ranges: (data.scale_ranges || []).map((r) => ({ id: uid(), ...r })),
        periods: (data.periods || []).map((p) => ({ ...p, id: p.id || uid() })),
        category_settings: { ...EMPTY_SETUP_STATE.category_settings, ...(data.category_settings || {}) },
        groups: (data.groups || []).map((g) => ({ ...g, id: g.id || uid() })),
        categories: (data.categories || []).map((c) => ({
          ...c, id: c.id || uid(),
          components: (c.components || []).map((co) => ({ ...co, id: co.id || uid() })),
        })),
      };
      setState(enriched);
      setOriginal(JSON.parse(JSON.stringify(enriched)));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [branchId, schoolYear?.id, classId, sectionId]);

  useEffect(() => { loadSetup(); }, [loadSetup]);

  const dirty = useMemo(() => JSON.stringify(state) !== JSON.stringify(original), [state, original]);

  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  /* ---------- Templates ---------- */
  const openTemplates = async () => {
    try {
      const calls = [];
      if (!templates.length) calls.push(api.get("/grading-setup/templates"));
      else calls.push(Promise.resolve({ data: { templates } }));
      calls.push(api.get("/grading-setup/my-templates"));
      const [defaults, mine] = await Promise.all(calls);
      if (!templates.length) setTemplates(defaults.data.templates || []);
      setMyTemplates(mine.data.templates || []);
    } catch (e) { toast.error(formatApiError(e)); return; }
    setShowTemplates(true);
  };
  const applyTemplate = (tpl) => {
    if (dirty && !window.confirm("This will replace your current unsaved setup. Continue?")) return;
    // Build old→new id maps so category.group_id references still resolve
    // after we regenerate ids for the new scope.
    const groupIdMap = {};
    const newGroups = (tpl.groups || []).map((g, i) => {
      const nid = uid();
      if (g.id) groupIdMap[g.id] = nid;
      return { ...g, id: nid, sort_order: i };
    });
    setState({
      settings: { ...EMPTY_SETUP_STATE.settings, ...(tpl.settings || {}) },
      scale_ranges: (tpl.scale_ranges || []).map((r) => ({ ...r, id: uid() })),
      periods: (tpl.periods || []).map((p, i) => ({ ...p, id: uid(), sort_order: i })),
      category_settings: { ...EMPTY_SETUP_STATE.category_settings, ...(tpl.category_settings || {}) },
      groups: newGroups,
      categories: (tpl.categories || []).map((c, i) => ({
        ...c, id: uid(), sort_order: i,
        // Remap the group reference so it points to the newly-generated group id.
        group_id: c.group_id && groupIdMap[c.group_id] ? groupIdMap[c.group_id] : (c.group_id || null),
        components: (c.components || []).map((co, j) => ({ ...co, id: uid(), sort_order: j })),
      })),
    });
    setShowTemplates(false);
    toast.success(`Applied template: ${tpl.name}`);
  };
  const saveAsTemplate = async (name) => {
    try {
      // Preserve `id` fields so category.group_id references remain valid
      // when the template is later re-applied. Templates live in their own
      // collection so the ids don't collide with any scope-level setup.
      const passThrough = (obj) => {
        if (!obj) return obj;
        if (obj.components) {
          return { ...obj, components: obj.components.map(passThrough) };
        }
        return { ...obj };
      };
      const { data } = await api.post("/grading-setup/my-templates", {
        name,
        description: "",
        settings: state.settings,
        scale_ranges: state.scale_ranges.map(passThrough),
        periods: state.periods.map(passThrough),
        category_settings: state.category_settings,
        groups: state.groups.map(passThrough),
        categories: state.categories.map(passThrough),
      });
      setMyTemplates((prev) => [data, ...prev]);
      toast.success(`Template "${name}" saved.`);
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };
  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/grading-setup/my-templates/${id}`);
      setMyTemplates((prev) => prev.filter((t) => t.id !== id));
      toast.success("Template deleted.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /* ---------- Mutators ---------- */
  const updSettings = (patch) => setState((s) => ({ ...s, settings: { ...s.settings, ...patch } }));
  const updCatSettings = (patch) => setState((s) => ({ ...s, category_settings: { ...s.category_settings, ...patch } }));

  const generatePeriods = (n) => {
    const count = Math.max(1, Math.min(12, Number(n) || 0));
    const pct = Math.round((100 / count) * 100) / 100;
    const list = Array.from({ length: count }, (_, i) => ({
      id: uid(),
      period_name: `${ordinal(i + 1)} ${count === 2 ? "Semester" : count === 3 || count > 4 ? "Term" : "Quarter"}`,
      period_percentage: i === count - 1 ? Math.round((100 - pct * (count - 1)) * 100) / 100 : pct,
      sort_order: i,
    }));
    setState((s) => ({ ...s, periods: list }));
  };
  const addPeriod = () => setState((s) => ({ ...s, periods: [...s.periods, { id: uid(), period_name: "", period_percentage: 0, sort_order: s.periods.length }] }));
  const updPeriod = (id, patch) => setState((s) => ({ ...s, periods: s.periods.map((p) => p.id === id ? { ...p, ...patch } : p) }));
  const delPeriod = (id) => setState((s) => ({ ...s, periods: s.periods.filter((p) => p.id !== id) }));

  const addScaleRow = () => setState((s) => ({ ...s, scale_ranges: [...s.scale_ranges, { id: uid(), code_or_value: "", description: "", min_percentage: 0, max_percentage: 0, sort_order: s.scale_ranges.length }] }));
  const updScaleRow = (id, patch) => setState((s) => ({ ...s, scale_ranges: s.scale_ranges.map((r) => r.id === id ? { ...r, ...patch } : r) }));
  const delScaleRow = (id) => setState((s) => ({ ...s, scale_ranges: s.scale_ranges.filter((r) => r.id !== id) }));

  const addGroup = () => setState((s) => ({ ...s, groups: [...s.groups, { id: uid(), group_name: "", description: "", sort_order: s.groups.length }] }));
  const updGroup = (id, patch) => setState((s) => ({ ...s, groups: s.groups.map((g) => g.id === id ? { ...g, ...patch } : g) }));
  const delGroup = (id) => setState((s) => ({ ...s, groups: s.groups.filter((g) => g.id !== id) }));

  const addCategory = () => {
    const id = uid();
    setState((s) => ({ ...s, categories: [...s.categories, { id, category_name: "", group_id: null, sort_order: s.categories.length, components: [] }] }));
    setExpanded((e) => ({ ...e, [id]: true }));
  };
  const updCategory = (id, patch) => setState((s) => ({ ...s, categories: s.categories.map((c) => c.id === id ? { ...c, ...patch } : c) }));
  const delCategory = (id) => setState((s) => ({ ...s, categories: s.categories.filter((c) => c.id !== id) }));
  const addComponent = (catId) => setState((s) => ({
    ...s, categories: s.categories.map((c) => c.id !== catId ? c : {
      ...c, components: [...c.components, { id: uid(), component_name: "", component_percentage: 0, sort_order: c.components.length }],
    }),
  }));
  const updComponent = (catId, compId, patch) => setState((s) => ({
    ...s, categories: s.categories.map((c) => c.id !== catId ? c : {
      ...c, components: c.components.map((co) => co.id === compId ? { ...co, ...patch } : co),
    }),
  }));
  const delComponent = (catId, compId) => setState((s) => ({
    ...s, categories: s.categories.map((c) => c.id !== catId ? c : {
      ...c, components: c.components.filter((co) => co.id !== compId),
    }),
  }));

  /* ---------- Save / Discard ---------- */
  const validate = () => {
    for (const p of state.periods) if (!p.period_name?.trim()) return "Every grading period needs a name";
    if (state.settings.grading_mode === "calculated" && state.periods.length > 0) {
      const total = totalPeriodPercent(state.periods);
      if (total !== 100) return `Grading periods must sum to 100% (got ${total}%)`;
    }
    if (state.category_settings.category_label === "custom" && !state.category_settings.custom_label?.trim()) return "Custom category label is empty";
    if (state.category_settings.grouping_enabled && state.category_settings.grouping_label === "custom" && !state.category_settings.custom_grouping_label?.trim()) return "Custom grouping label is empty";
    for (const g of state.groups) if (!g.group_name?.trim()) return "Every group needs a name";
    for (const c of state.categories) {
      if (!c.category_name?.trim()) return "Every subject needs a name";
      for (const co of c.components) if (!co.component_name?.trim()) return `Every component in ${c.category_name} needs a name`;
      if (state.settings.grading_mode === "calculated" && c.components.length > 0) {
        const t = totalComponentPercent(c.components);
        if (t !== 100) return `Components in "${c.category_name}" must sum to 100% (got ${t}%)`;
      }
    }
    const scaleErr = validateScaleRanges(state.settings.grading_scale_type, state.scale_ranges, {
      customDescEnabled: state.settings.custom_description_enabled,
      customRangeEnabled: state.settings.custom_percentage_range_enabled,
    });
    if (scaleErr) return scaleErr;
    if (state.settings.grading_mode === "direct" && !state.settings.direct_assessment_level) return "Pick a Direct Assessment Level";
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    if (!classId && !sectionId) {
      if (!window.confirm("This will save the configuration for ALL levels in this branch. Continue?")) return;
    } else if (classId && !sectionId) {
      const cn = classes.find((c) => c.id === classId)?.name || "this class";
      if (!window.confirm(`This will save the configuration for ALL sections of ${cn}. Continue?`)) return;
    }
    setSaving(true);
    try {
      await api.put("/grading-setup", {
        branch_id: branchId,
        school_year_id: schoolYear?.id || undefined,
        class_id: classId || null,
        section_id: sectionId || null,
        scope_type: scopeType,
        settings: state.settings,
        scale_ranges: state.scale_ranges.map(({ id: _i, ...r }) => r),
        periods: state.periods.map(({ id, period_name, period_percentage, sort_order }) => ({ id, period_name, period_percentage, sort_order })),
        category_settings: state.category_settings,
        groups: state.groups.map(({ id, group_name, description, sort_order }) => ({ id, group_name, description, sort_order })),
        categories: state.categories.map(({ id, category_name, group_id, sort_order, components }) => ({
          id, category_name, group_id: group_id || null, sort_order,
          components: components.map(({ id: cid, component_name, component_percentage, sort_order: so }) => ({
            id: cid, component_name, component_percentage, sort_order: so,
          })),
        })),
      });
      toast.success("Grading setup saved");
      await loadSetup();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const handleDiscard = () => {
    if (!window.confirm("Discard all unsaved changes?")) return;
    setState(JSON.parse(JSON.stringify(original)));
    toast.info("Changes discarded");
  };

  /* ---------- Filter scope guard ---------- */
  const guardChangeFilter = (cb) => {
    if (dirty && !window.confirm("You have unsaved changes. Discard and continue?")) return;
    setState(JSON.parse(JSON.stringify(original)));
    cb();
  };

  /* ---------- Derived labels ---------- */
  const categoryLabel = state.category_settings.category_label === "custom"
    ? (state.category_settings.custom_label || "Categor")
    : state.category_settings.category_label === "subjects" ? "Subject"
    : state.category_settings.category_label === "learning_areas" ? "Learning Area"
    : "Competency";
  const categoryPlural = (state.category_settings.category_label === "custom" && state.category_settings.custom_label)
    ? state.category_settings.custom_label
    : state.category_settings.category_label === "subjects" ? "Subjects"
    : state.category_settings.category_label === "learning_areas" ? "Learning Areas"
    : "Competencies";

  return (
    <>
      <div className="pb-28">
        {/* Header */}
        <div className="mb-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Grading Setup</h1>
            <p className="text-brand-inkMuted mt-0.5 text-sm max-w-3xl">Set grading terms, choose your category label, then create {categoryPlural.toLowerCase()} and assessment components. Tasks are added in Grades.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={openTemplates} className="rounded-2xl h-10" data-testid="gs-templates-btn">
              <Sparkles className="w-4 h-4 mr-1.5" /> Templates
            </Button>
            <Button variant="outline" onClick={() => setPreviewOpen(true)} className="rounded-2xl h-10" data-testid="gs-preview-btn">
              <Eye className="w-4 h-4 mr-1.5" /> Preview
            </Button>
          </div>
        </div>

        <ScopeFilterBar
          branchId={branchId}
          branches={branchesForSelectedSchool}
          onBranchChange={(v) => guardChangeFilter(() => setSelectedBranchId(v))}
          classId={classId}
          classes={classes}
          onClassChange={(v) => guardChangeFilter(() => setClassId(v))}
          sectionId={sectionId}
          sections={sections}
          onSectionChange={(v) => guardChangeFilter(() => setSectionId(v))}
          schoolYear={schoolYear}
        />

        <div className="mb-4 rounded-2xl border-2 border-[#F59E0B]/40 bg-[#FFF7E6] px-4 py-3 text-sm" data-testid="gs-scope-notice">
          <p className="font-heading font-semibold text-[#B45309]">
            Current Setup Scope: {scopeType === "all_levels" ? "All Levels" : scopeType === "class_level" ? `Class · ${classes.find((c) => c.id === classId)?.name}` : `Section · ${sections.find((s) => s.id === sectionId)?.name}`}
            {selectedBranch?.name && <> · Branch: {selectedBranch.name}</>}
          </p>
          <p className="text-[#92400E] text-xs mt-0.5">Changes saved here apply only to this scope. Section settings override class-level; class-level overrides school-wide.</p>
        </div>

        {loading ? (
          <p className="text-brand-inkMuted p-6">Loading...</p>
        ) : (
          <div className="space-y-4">
            <Step0Settings
              settings={state.settings}
              onSettingsChange={updSettings}
              scaleRanges={state.scale_ranges}
              onAddScaleRow={addScaleRow}
              onUpdScaleRow={updScaleRow}
              onDelScaleRow={delScaleRow}
            />
            <Step1Periods
              periods={state.periods}
              gradingMode={state.settings.grading_mode}
              onGenerate={generatePeriods}
              onAdd={addPeriod}
              onUpdate={updPeriod}
              onDelete={delPeriod}
            />
            <Step2CategoryLabel
              categorySettings={state.category_settings}
              onChange={updCatSettings}
            />
            <Step3Grouping
              categorySettings={state.category_settings}
              onChange={updCatSettings}
              groups={state.groups}
              onAddGroup={addGroup}
              onUpdateGroup={updGroup}
              onDeleteGroup={delGroup}
              categoryPlural={categoryPlural}
            />
            <Step4Subjects
              categories={state.categories}
              groups={state.groups}
              categorySettings={state.category_settings}
              gradingMode={state.settings.grading_mode}
              categoryLabel={categoryLabel}
              categoryPlural={categoryPlural}
              expanded={expanded}
              setExpanded={setExpanded}
              onAddCategory={addCategory}
              onUpdateCategory={updCategory}
              onDeleteCategory={delCategory}
              onAddComponent={addComponent}
              onUpdateComponent={updComponent}
              onDeleteComponent={delComponent}
            />
          </div>
        )}
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} onDiscard={handleDiscard} />

      <TemplatesDialog
        open={showTemplates}
        onClose={() => setShowTemplates(false)}
        templates={templates}
        onApply={applyTemplate}
        categoryPlural={categoryPlural}
        myTemplates={myTemplates}
        onSaveTemplate={saveAsTemplate}
        onDeleteTemplate={deleteTemplate}
      />

      <PreviewReportCardDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        setup={state}
        categoryLabel={categoryPlural}
      />

      <style>{`
        .gs-select {
          width: 100%; height: 2.75rem; border-radius: 0.75rem;
          border: 1px solid rgba(37, 99, 235, 0.2); background: #fff;
          padding: 0 0.75rem; font-size: 0.9rem; color: hsl(var(--foreground));
        }
        .gs-select:focus-visible { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring)); }
      `}</style>
    </>
  );
}
